import React, { useState, useEffect } from 'react';
import { Box, Paper, Grid, Typography, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const ColoredCellRenderer = ({ value, colDef, data, api }) => {
    const getBackgroundColor = () => {
        if (!value || value === 0) return 'transparent';
        const field = colDef.field;
        if (field === 'Data' || field === 'Paes') return 'transparent';

        const allData = [];
        api.forEachNode((node) => {
            const nodeValue = node.data[field];
            if (nodeValue && nodeValue > 0) {
                allData.push(nodeValue);
            }
        });

        if (allData.length === 0) return 'transparent';

        const mean = allData.reduce((sum, val) => sum + val, 0) / allData.length;
        const variance = allData.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / allData.length;
        const stdDev = Math.sqrt(variance);

        if (field === 'alface') {
            if (value > 0.040) return 'red';
            if (value < mean - stdDev) return 'yellow';
            return 'transparent';
        }

        if (value > mean + stdDev) return 'red';
        if (value < mean - stdDev) return 'yellow';

        return 'transparent';
    };

    return (
        <div style={{
            backgroundColor: getBackgroundColor(),
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 4px'
        }}>
            {value}
        </div>
    );
};

export default function VegetaisMovimentacao() {
    const [statsRowData, setStatsRowData] = useState([]);
    const [alertas, setAlertas] = useState([]);
    const [dailyDataRowData, setDailyDataRowData] = useState([]);
    const [diasAnalise, setDiasAnalise] = useState(90);
    const [diasAnaliseInput, setDiasAnaliseInput] = useState(90);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { authenticatedFetch, logout } = useAuth();

    const vegetaisList = ['tomate', 'cebola', 'pimentao', 'pepino', 'alface'];

    const [statsColumnDefs] = useState([
        { headerName: 'Estatística', field: 'estatistica', pinned: 'left', width: 185 },
        ...vegetaisList.map(vegetal => ({
            headerName: vegetal.charAt(0).toUpperCase() + vegetal.slice(1),
            field: vegetal,
            width: 85
        }))
    ]);

    const [dailyDataColumnDefs] = useState([
        { headerName: 'Data', field: 'Data', width: 120, cellRenderer: ColoredCellRenderer },
        { headerName: 'Tomate', field: 'tomate', width: 80, cellRenderer: ColoredCellRenderer },
        { headerName: 'Cebola', field: 'cebola', width: 80, cellRenderer: ColoredCellRenderer },
        { headerName: 'Pimentão', field: 'pimentao', width: 80, cellRenderer: ColoredCellRenderer },
        { headerName: 'Pepino', field: 'pepino', width: 80, cellRenderer: ColoredCellRenderer },
        { headerName: 'Alface', field: 'alface', width: 80, cellRenderer: ColoredCellRenderer },
        { headerName: 'Pães', field: 'Paes', width: 80, cellRenderer: ColoredCellRenderer },
    ]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDiasAnalise(diasAnaliseInput);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [diasAnaliseInput]);

    const gerarAlertas = (estatisticasData, mappedData) => {
        const rowMediaMaisDP = estatisticasData.find(
            r => r.estatistica === 'Média + Desvio Padrão'
        );

        const sorted = [...mappedData].sort((a, b) => new Date(a.Data) - new Date(b.Data));
        const ultimoDia = sorted[sorted.length - 1];

        if (!rowMediaMaisDP || !ultimoDia) {
            setAlertas(['Nenhum alerta para exibir.']);
            return;
        }

        const hora = new Date().getHours();
        const saudacao = hora >= 5 && hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';

        const paes = ultimoDia.Paes || 0;
        const novoAlertas = [];

        const gerarFrase = (nome, threshold, valor) => {
            if (!valor || valor <= threshold + 0.0001) return null;
            const diff = valor - threshold;
            const kg = diff * paes;
            return (
                `Peso padrão do ${nome} ${threshold.toFixed(3)}, a saída foi de ${valor.toFixed(3)} ` +
                `sendo a diferença ${diff.toFixed(3)}. Saiu ${paes.toFixed(1)} pães, ` +
                `(${diff.toFixed(3)}*${paes.toFixed(1)}) saindo assim ${kg.toFixed(3)} kg a mais do que deveria.`
            );
        };

        const vegetaisNormais = ['tomate', 'cebola', 'pimentao', 'pepino'];
        vegetaisNormais.forEach(vegetal => {
            const threshold = parseFloat(rowMediaMaisDP[vegetal]) || 0;
            const valor = ultimoDia[vegetal] || 0;
            const nome = vegetal.charAt(0).toUpperCase() + vegetal.slice(1);
            const frase = gerarFrase(nome, threshold, valor);
            if (frase) novoAlertas.push(frase);
        });

        const fraseAlface = gerarFrase('Alface', 0.040, ultimoDia.alface || 0);
        if (fraseAlface) novoAlertas.push(fraseAlface);

        setAlertas(
            novoAlertas.length > 0
                ? [saudacao + '.', ...novoAlertas]
                : ['Nenhum alerta para exibir.']
        );
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await authenticatedFetch(`${API_BASE_URL}/api/vegetais-verificacao/?dias=${diasAnalise}`, {
                    method: 'POST',
                });

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const data = await response.json();

                const estatisticasData = data.estatisticas || [];
                const tabelaProporcoesData = data.tabela_proporcoes || [];

                if (estatisticasData.length > 0 && tabelaProporcoesData.length > 0) {
                    const filteredData = tabelaProporcoesData.filter(row =>
                        (row.tomate && row.tomate > 0) ||
                        (row.cebola && row.cebola > 0) ||
                        (row.pimentao && row.pimentao > 0) ||
                        (row.pepino && row.pepino > 0) ||
                        (row.alface && row.alface > 0)
                    );

                    const mappedData = filteredData.map(row => ({
                        Data: row.data,
                        tomate: row.tomate || 0,
                        cebola: row.cebola || 0,
                        pimentao: row.pimentao || 0,
                        pepino: row.pepino || 0,
                        alface: row.alface || 0,
                        Paes: row.Paes || 0,
                    }));

                    setDailyDataRowData(mappedData);

                    const formattedStats = estatisticasData.map(statRow => {
                        const formattedRow = { estatistica: statRow.estatistica };
                        vegetaisList.forEach(vegetal => {
                            formattedRow[vegetal] = statRow[vegetal] || 'N/A';
                        });
                        return formattedRow;
                    });

                    setStatsRowData(formattedStats);
                    gerarAlertas(estatisticasData, mappedData);

                } else {
                    setDailyDataRowData([]);
                    setStatsRowData([]);
                    setAlertas(['Nenhum dado encontrado para o período selecionado.']);
                }
            } catch (error) {
                setError(error.message);
                setDailyDataRowData([]);
                setStatsRowData([]);
                setAlertas([`Erro ao carregar dados: ${error.message}`]);
                if (error.message.includes('Token')) {
                    logout();
                } else {
                    alert("Falha ao buscar os dados:", error);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [diasAnalise]);

    const handleDiasChange = (event) => {
        const newValue = parseInt(event.target.value) || 90;
        setDiasAnaliseInput(newValue);
    };

    const today = new Date();
    const maxDate = new Date('2023-01-01');
    const oneDayInMs = 1000 * 60 * 60 * 24;
    const maxDays = Math.floor((today - maxDate) / oneDayInMs);

    return (
        <Box sx={{ flexGrow: 1, p: 4 }}>
            {error && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
                    <Typography variant="body2">Erro: {error}</Typography>
                </Box>
            )}

            {loading && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', color: 'info.contrastText', borderRadius: 1 }}>
                    <Typography variant="body2">Carregando dados...</Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <Typography>Número de dias para análise</Typography>
                <TextField
                    type="number"
                    value={diasAnaliseInput}
                    disabled={loading}
                    onChange={(e) => {
                        let newValue = parseInt(e.target.value, 10);
                        if (isNaN(newValue) || newValue < 7) newValue = 7;
                        if (newValue > maxDays) newValue = maxDays;
                        setDiasAnaliseInput(newValue);
                    }}
                    slotProps={{ input: { min: 7, max: maxDays } }}
                    sx={{
                        width: '100%',
                        maxWidth: 120,
                        '& input[type="number"]': { '-moz-appearance': 'textfield' },
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            '-webkit-appearance': 'inner-spin-button',
                            opacity: 1,
                        },
                    }}
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 4, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 50%', maxWidth: 615 }} className="ag-theme-alpine">
                    <AgGridReact
                        rowData={statsRowData}
                        columnDefs={statsColumnDefs}
                        loading={loading}
                        suppressSizeToFit={true}
                        suppressAutoSize={true}
                        domLayout="autoHeight"
                        suppressHorizontalScroll={true}
                        defaultColDef={{ sortable: false, filter: false, resizable: false }}
                    />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 4, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 50%', mt: -2, minWidth: 300 }}>
                    <TextField
                        multiline
                        rows={4}
                        fullWidth
                        value={alertas.length > 0 ? alertas.join('\n') : 'Nenhum alerta para exibir.'}
                        slotProps={{ input: { readOnly: true } }}
                        sx={{ maxWidth: 615 }}
                    />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: 'red', border: '1px solid #ccc' }} />
                    <Typography variant="caption">
                        Acima da média + desvio padrão (Alface &gt; 0.040)
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: 'yellow', border: '1px solid #ccc' }} />
                    <Typography variant="caption">
                        Abaixo da média - desvio padrão
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ maxWidth: 600, overflowX: 'auto' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                    <Box sx={{ flex: '1 1 650px' }} className="ag-theme-alpine">
                        <AgGridReact
                            rowData={dailyDataRowData}
                            columnDefs={dailyDataColumnDefs}
                            loading={loading}
                            domLayout="autoHeight"
                            suppressHorizontalScroll={true}
                            onGridReady={(params) => {
                                setTimeout(() => params.api.refreshCells(), 100);
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}